import { getDb } from '../db';
import { DEFAULT_SETTINGS } from './defaults';
import {
  SEED_ABBREVS,
  SEED_CATEGORIES,
  SEED_PHRASES,
  SEED_QUICK_FIRES,
} from './content';

export { DEFAULT_SETTINGS } from './defaults';
export {
  SEED_ABBREVS,
  SEED_CATEGORIES,
  SEED_KERNVOKABULAR,
  SEED_PHRASES,
  SEED_QUICK_FIRES,
} from './content';

/**
 * Populate an empty database. Idempotent and additive-free: it does nothing at all
 * once quick fires exist, so a launch can never overwrite a caregiver's edits or
 * resurrect a phrase they deleted.
 *
 * Quick fires are the sentinel because they are the one set that always exists —
 * they are structural rather than editable.
 */
export async function seedIfEmpty(): Promise<boolean> {
  try {
    seededFresh = await seed();
    return seededFresh;
  } finally {
    settleSeed();
  }
}

let settleSeed: () => void = () => {};
let seededFresh = false;

/**
 * Whether *this launch* was the one that populated an empty database.
 *
 * The signal behind the first-run restore offer (4.7). It needs no stored flag and cannot go stale, because
 * it is true exactly once in the life of an install: the next launch finds quick fires and seeds nothing. So
 * "have we offered this already?" never has to be remembered — the question stops being askable.
 *
 * Only meaningful after `seedSettled`.
 */
export function wasFreshInstall(): boolean {
  return seededFresh;
}

/**
 * Resolves once seeding has finished, successfully or not.
 *
 * A first launch is a race: the app renders before the database has content (deliberately — nothing may
 * sit between the person and a keyboard), so anything mounted at t=0 can read an empty store. The bar is
 * the one surface that does, and an empty bar means no `Ja`, no `Nein`, no `Stopp` on the very first
 * launch until something happens to remount it.
 *
 * Consumers read immediately *and* re-read on this, rather than waiting for it: a context that never
 * seeds at all must still show whatever is already stored.
 */
export const seedSettled: Promise<void> = new Promise<void>((resolve) => {
  settleSeed = resolve;
});

async function seed(): Promise<boolean> {
  const db = await getDb();

  if ((await db.count('quickFires')) > 0) return false;

  const tx = db.transaction(
    ['settings', 'quickFires', 'categories', 'phrases', 'abbrevs'],
    'readwrite',
  );

  const existingSettings = await tx.objectStore('settings').get('settings');
  if (!existingSettings) await tx.objectStore('settings').put(DEFAULT_SETTINGS, 'settings');

  const quickFires = tx.objectStore('quickFires');
  for (const row of SEED_QUICK_FIRES) await quickFires.put(row);

  const categories = tx.objectStore('categories');
  for (const row of SEED_CATEGORIES) await categories.put(row);

  const phrases = tx.objectStore('phrases');
  for (const row of SEED_PHRASES) await phrases.put(row);

  const abbrevs = tx.objectStore('abbrevs');
  for (const row of SEED_ABBREVS) await abbrevs.put(row);

  await tx.done;
  return true;
}
